import React, {ReactNode, useContext, useMemo} from "react";
import {CoppiaTimbrature} from "@commons/mapping/CoppiaTimbrature";
import {InputCleave, InputEx} from "@components/wrapperComponents/InputEx";
import TooltipEx from "@components/Tooltip";
import css from "../pannelloGiornataPLUS.module.css"
import {useFieldArray, useFormContext, useWatch} from "react-hook-form";
import CheckboxEx from "@components/wrapperComponents/CheckboxEx";
import {useAppSelector} from "@hooks/redux-hooks/hooks";
import {selectSitePreferences} from "@commons/core.slice";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {PresenzeContext} from "@commons/hooks/usePresenze";

export type TimbratureType = keyof Pick<PresenzeGiornataPlusRecord, 'previste' | 'effettiveOriginali' | 'effettive' | 'arrotondate' | 'effettiveFlex'>;

interface TimbratureProps {
    tipoTimbrature: TimbratureType,
    showLabels?: boolean,
    labelRowTimbrature: string,
    disabled?: boolean,
    children: ReactNode,
    effettive?: CoppiaTimbrature[],
    effettiveOrig?: CoppiaTimbrature[],
    onChangeOraInizio?: (hhmm: string, index: number | null) => void,
    onChangeOraFine?: (hhmm: string, index: number | null) => void,
}

const maxTimb = 10;
const inputCleaveOptions = {
    time: true, timePattern: ['h', 'm'], blocks: [2, 2],
    delimiter: ':', timeFormat: '24'
};

const TimbraturePLUS: React.FC<TimbratureProps> = ({
                                                       tipoTimbrature,
                                                       showLabels = true, labelRowTimbrature, disabled = false,
                                                       children,
                                                       effettive,
                                                       effettiveOrig,
                                                       onChangeOraInizio, onChangeOraFine,
                                                   }) => {
    const {listaTerminali} = useContext(PresenzeContext);
    const {control,} = useFormContext<PresenzeGiornataPlusRecord>();
    const sitePreferences = useAppSelector(selectSitePreferences);
    const mostra5Coppia: boolean | undefined = sitePreferences.presenzeOptions && sitePreferences.presenzeOptions.mostra5Coppia;
    // const mostra5Coppia: boolean | undefined = true;

    /**
     * nullPos determina la possibilità di editare le timbrature effettive nel caso sia presente un entrata ma non uscita e viceversa
     * se null, timbratura non editabile, se elemento valorizzato allora la timbratura è editabile
     * N.B. il numero indica la posizione nell'array delle timbrature effettive originali.
     */
    const nullPos = useMemo<(number | null)[]>((): (number | null)[] => {
        if (tipoTimbrature !== "effettive")
            return [];

        const nullPos: (number | null)[] = [];
        if (effettiveOrig) {
            let index = 0;
            for (const timb of effettiveOrig) {
                if (timb.utilizzaEntrata) {
                    if (CoppiaTimbrature.isValidOne(timb)) {
                        nullPos.push(CoppiaTimbrature.getEntrata(timb) === null ? index : null);
                    }
                }
                ++index;
                if (timb.utilizzaUscita) {
                    if (CoppiaTimbrature.isValidOne(timb)) {
                        nullPos.push(CoppiaTimbrature.getUscita(timb) === null ? index : null);
                    }
                }
                ++index;
            }
        }
        for (let ind0 = nullPos.length; ind0 < maxTimb; ++ind0) {
            nullPos.push(null);
        }
        return nullPos;
    }, [tipoTimbrature, effettiveOrig]);

    //(tipoTimbrature === 'effettive') && console.log(nullPos);

    const getBgColorTimbratura = (codTerminale: string | null): string | undefined => {
        if (listaTerminali && codTerminale) {
            return listaTerminali.find(terminale => terminale.codTerminale === codTerminale)?.bgColor;
        }
        return undefined;
    };

    const getTooltipTimbrature = (index: number, verso: 'entrata' | 'uscita'): string | boolean => {

        let timbrature: CoppiaTimbrature[] | null;
        switch (tipoTimbrature) {
            case 'effettiveOriginali':
                timbrature = effettiveOrig || null;
                break;
            case 'effettive':
                timbrature = effettive || null;
                break;
            default:
                timbrature = null;
                break;
        }

        if (timbrature && timbrature.length > 0 && timbrature[index]) {
            const timbratura: CoppiaTimbrature = timbrature[index];
            if (verso === 'entrata')
                return timbratura.entrataTerminale === 'null' ? false : listaTerminali.find(terminale => terminale.codTerminale === timbratura.entrataTerminale)?.descrizione || '';
            else {
                return timbratura.uscitaTerminale === 'null' ? false : (listaTerminali.find(terminale => terminale.codTerminale === timbratura.uscitaTerminale)?.descrizione) || '';
            }
        } else return false;
    }

    const timbrature = useWatch({
        name: tipoTimbrature,
        control
    });
    const formatOrario = (v: string | null, index: number) => {
        if (!v)
            return true; // todo verificare!

        if (timbrature[index].entrata || timbrature[index].uscita)
            return (v && (v.length === 4));
        else
            return true;
    }

// useFieldArray permette di gestire form dinamici sulla base di un array di oggetti
// vedi: https://www.youtube.com/watch?v=4MrbfGSFY2A
    const {fields} = useFieldArray(
        {
            name: tipoTimbrature,
            control,
            rules: {validate: () => true}
        });

    return <div className={`${css.gridTimbrature} ${!mostra5Coppia ? css.grid4Timbrature : css.grid5Timbrature}`}>
        <div className={css.labelTimbrature}>{labelRowTimbrature}</div>
        <div className={`${css.containerTimbrature} ${!mostra5Coppia ? css.container4Timbrature : css.container5Timbrature}`}>
            {fields
                .filter((_field, index) => (index < 4 || (tipoTimbrature !== 'previste' && mostra5Coppia)))
                .map((field, index) => {

                    const inputEntrata = <InputCleave<PresenzeGiornataPlusRecord>
                        className="h-8 my-1 sm:mb-0"
                        label={showLabels ? `${index + 1}° entrata` : ''}
                        labelClassName="md:text-xs 2xl:text-sm mb-0"
                        name={`${tipoTimbrature}.${index}.entrata` as const}
                        placeholder="hh:mm"
                        disabled={(tipoTimbrature === "effettive" && (nullPos[index * 2] === null)) || disabled}
                        control={control}
                        onChange={(v: string) => onChangeOraInizio && onChangeOraInizio(v, nullPos[index * 2])}
                        options={inputCleaveOptions}
                        rules={{
                            validate: {
                                format: v => formatOrario(v as string | null, index) || 'Utilizzare il formato hh:mm',
                            }
                        }}
                        addOnAfter={tipoTimbrature === 'effettiveOriginali'}
                        style={effettive && {backgroundColor: getBgColorTimbratura(effettive[index].entrataTerminale)}}
                    />

                    const inputUscita = <InputCleave<PresenzeGiornataPlusRecord>
                        className="h-8 my-1 sm:mb-0"
                        label={showLabels ? `${index + 1}° uscita` : ''}
                        labelClassName="md:text-xs 2xl:text-sm mb-0"
                        name={`${tipoTimbrature}.${index}.uscita`}
                        placeholder="hh:mm"
                        disabled={(tipoTimbrature === "effettive" && (nullPos[(index * 2) + 1] === null)) || disabled}
                        control={control}
                        onChange={(v: string) => onChangeOraFine && onChangeOraFine(v, nullPos[(index * 2) + 1])}
                        options={inputCleaveOptions}
                        rules={{
                            validate: {
                                format: v => formatOrario(v as string | null, index) || 'Utilizzare il formato hh:mm',
                            }
                        }}
                        addOnAfter={tipoTimbrature === 'effettiveOriginali'}
                        style={effettive && {backgroundColor: getBgColorTimbratura(effettive[index].uscitaTerminale)}}
                    />

                    return <div key={field.id} className="grid grid-cols-subgrid col-span-2">
                        <TooltipEx tooltip={getTooltipTimbrature(index, 'entrata')}>
                            <div>
                                <InputEx input={inputEntrata} name="stub" addOnContainerClass="items-end"
                                         addOnAfter={tipoTimbrature === 'effettiveOriginali' &&
                                             <CheckboxEx name={`${tipoTimbrature}.${index}.utilizzaEntrata`} control={control} className="h-2"/>}>
                                </InputEx>
                            </div>
                        </TooltipEx>
                        <TooltipEx tooltip={getTooltipTimbrature(index, 'uscita')}>
                            <div>
                                <InputEx input={inputUscita} name="stub" addOnContainerClass="items-end"
                                         addOnAfter={tipoTimbrature === 'effettiveOriginali' &&
                                             <CheckboxEx name={`${tipoTimbrature}.${index}.utilizzaUscita`} control={control} className="h-2"/>}>
                                </InputEx>
                            </div>
                        </TooltipEx>
                    </div>
                })}
            {children}
        </div>
    </div>
}

export default TimbraturePLUS;